import { GallerySection, Specimen } from "../galleryKit";

const SEMANTIC_TOKENS: { name: string; role: string }[] = [
    { name: "canvas", role: "App and page background" },
    { name: "sunken", role: "Pressed fill, indicators" },
    { name: "elevated", role: "Panels floating on canvas" },
    { name: "paper", role: "Public cards" },
    { name: "well", role: "Grouped icon controls" },
    { name: "field", role: "Input background" },
    { name: "inset", role: "Recessed tool output" },
    { name: "recess", role: "Body band under a header" },
    { name: "line", role: "Default border" },
    { name: "line-strong", role: "Input / popover border" },
    { name: "accent", role: "Selection, emphasis" },
    { name: "info", role: "Multi-select, links" },
    { name: "danger", role: "Destructive, invalid" },
    { name: "warn", role: "Caution" },
    { name: "good", role: "Success, on" },
];

const INK_TOKENS: { name: string; role: string }[] = [
    { name: "black", role: "Ink. Use /30–/80 for hierarchy" },
    { name: "white", role: "Inverse of ink" },
    { name: "gray", role: "Chip / menu fill" },
    { name: "gray1", role: "Strong neutral" },
    { name: "lightblue", role: "Pre-prompt tint" },
];

// Literal class names so Tailwind keeps them.
const SWATCH_CLASS: Record<string, string> = {
    canvas: "bg-canvas", sunken: "bg-sunken", elevated: "bg-elevated", paper: "bg-paper", well: "bg-well",
    field: "bg-field", inset: "bg-inset", recess: "bg-recess", line: "bg-line", "line-strong": "bg-line-strong", accent: "bg-accent",
    info: "bg-info", danger: "bg-danger", warn: "bg-warn", good: "bg-good",
    black: "bg-black", white: "bg-white", gray: "bg-gray", gray1: "bg-gray1", lightblue: "bg-lightblue",
};

const SPACING: { token: string; value: string; width: string }[] = [
    { token: "nav", value: "0.5rem", width: "w-nav" },
    { token: "1s", value: "0.5rem", width: "w-1s" },
    { token: "1", value: "0.75rem", width: "w-1" },
    { token: "2", value: "1.5rem", width: "w-2" },
    { token: "3", value: "2.25rem", width: "w-3" },
    { token: "4", value: "4.5rem", width: "w-4" },
    { token: "5", value: "11.25rem", width: "w-5" },
    { token: "6", value: "28.5rem", width: "w-6" },
];

const TEXT_ROLES: { cls: string; use: string; sample: string }[] = [
    { cls: "display", use: "Marketing hero only", sample: "Aa" },
    { cls: "h2", use: "Modal and page titles", sample: "Page title" },
    { cls: "title", use: "Serif section title on dashboard-like pages", sample: "Section title" },
    { cls: "hsub", use: "Entry names, sub-headings", sample: "Entry name" },
    { cls: "b", use: "Body copy", sample: "Body text reads at a comfortable size and line height." },
    { cls: "l", use: "Control text: buttons, inputs, rows", sample: "Control label" },
    { cls: "l2", use: "Dense list labels", sample: "List item label" },
    { cls: "small", use: "Compact labels inside controls", sample: "Compact label" },
    { cls: "caption", use: "Quiet group label above a block", sample: "Group caption" },
    { cls: "eyebrow", use: "Small label above a title", sample: "Eyebrow" },
    { cls: "meta", use: "Type / timestamp line on entries", sample: "Type · 2026-01-01" },
    { cls: "hint", use: "Secondary helper text", sample: "Helper text" },
    { cls: "label-xs", use: "Dense admin and timeline labels", sample: "Label" },
    { cls: "serif", use: "Long-form reading text", sample: "Long-form reading text set in Source Serif." },
    { cls: "error-text", use: "Inline validation and request errors", sample: "Something went wrong" },
    { cls: "link", use: "Inline links; link-muted / link-danger for small text actions", sample: "A link" },
];

const Swatches = ({ tokens }: { tokens: { name: string; role: string }[] }) => (
    <div className="flex flex-row flex-wrap gap-1">
        {tokens.map((t) => (
            <div key={t.name} className="swatch">
                <div className={`swatch-chip ${SWATCH_CLASS[t.name]}`} />
                <code className="small">{t.name}</code>
                <span className="hint">{t.role}</span>
            </div>
        ))}
    </div>
);

export const FoundationsSection = () => (
    <GallerySection
        id="foundations"
        title="Foundations"
        intro="Tokens, spacing and text roles every component class is built from. Change a value here and every component follows."
    >
        <Specimen
            name="How to style a component"
            classes={["component", "component-variant"]}
            usage="Each visual component owns one class in src/styles (plus modifier classes for its variants). The class carries colour, border, radius, typography and states; the call site only places it."
            rules={[
                "Look for an existing class here first. Reusing one is the default; a near match beats a new class.",
                "Inline Tailwind at a call site is for placement only: flex, gap, width, margin, position, overflow, z-index.",
                "Never branch on appState.theme for styling. Use the tokens below; for the rare theme-specific value use the dark: variant inside the class.",
                "New component? Add its class to the CSS file for its area with @apply on tokens, a one-line comment, and a specimen on this page.",
                "Variants are modifier classes (.btn-ghost, .row-selected) applied conditionally, never assembled from string fragments.",
                "Object views follow one anatomy: header (kind · date, title + actions, fields), then body. Header and single-line fields sit on the surface; each rich-text field is a full-width recessed band (.entry-field-rich, bg-recess): slightly darker in both themes; body headings stay below the title in size.",
            ]}
            code={`
// good: component class + placement
<button className="btn btn-ghost ml-auto">Save</button>

// avoid: look defined at the call site
<button className="border rounded bg-black/10 hover:bg-black/20 px-1 ml-auto">Save</button>`}
            demo={() => <p className="hint">This page itself is built from these rules.</p>}
        />

        <Specimen
            name="Semantic colour tokens"
            classes={["bg-canvas", "border-line", "text-accent", "…"]}
            usage="Defined per theme in tailwind.config.ts (tw-colors). Use them through component classes; reach for them directly only inside src/styles."
            demo={() => <Swatches tokens={SEMANTIC_TOKENS} />}
        />

        <Specimen
            name="Ink and legacy colours"
            classes={["text-black", "text-black/40", "bg-gray"]}
            usage="black is always the ink colour (it inverts in dark mode), so text-black/40 is a quiet label in both themes. Tailwind's own grays (text-gray-500, bg-neutral-800) do not follow the theme; don't use them."
            demo={() => <Swatches tokens={INK_TOKENS} />}
        />

        <Specimen
            name="Spacing scale"
            classes={["p-1", "gap-1s", "h-3"]}
            usage="The spacing scale is replaced, not extended: p-1 is 0.75rem and h-3 is 2.25rem. Default tokens such as gap-1.5, px-4 (4.5rem here) or w-56 behave differently or don't exist; use an arbitrary value like w-[14rem] for anything off-scale."
            demo={() => (
                <div className="flex flex-col gap-[.35rem]">
                    {SPACING.map((s) => (
                        <div key={s.token} className="flex flex-row items-center gap-1">
                            <code className="small w-[2rem]">{s.token}</code>
                            <span className="hint w-[5rem]">{s.value}</span>
                            <div className={`spacing-bar ${s.width}`} />
                        </div>
                    ))}
                </div>
            )}
        />

        <Specimen
            name="Text roles"
            classes={TEXT_ROLES.map((r) => r.cls)}
            usage="Pick text by role, not by size. Hierarchy within a role comes from ink opacity (text-black/40…/80)."
            demo={() => (
                <div className="flex flex-col gap-1">
                    {TEXT_ROLES.map((r) => (
                        <div key={r.cls} className="flex flex-row items-baseline gap-1 min-w-0">
                            <code className="small w-[6rem] shrink-0 text-black/50">.{r.cls}</code>
                            <div className="min-w-0 flex-grow overflow-hidden">
                                <span className={r.cls === "display" ? "display !text-[3rem]" : r.cls}>{r.sample}</span>
                            </div>
                            <span className="hint shrink-0 hidden md:block">{r.use}</span>
                        </div>
                    ))}
                </div>
            )}
        />
    </GallerySection>
);
